import React, { useState } from "react";
import styled from "styled-components";

import { gradeLevelList, gradeLevelMeta } from "@examspark/common-meta";
import { Button, SelectInput, VSpace } from "@examspark/common-ui";
import { IDSK, Classroom, Course } from "@examspark/types";

import { useQueryGetAllCalendarsForCourse } from "../../api/operations/calendar";
import { useMutationUpdateClassroom } from "../../api/operations/classroom";
import { useQueryGetCoursesBySubject } from "../../api/operations/course";
import SetupPrompt from "../SetupPrompt";

import CourseCalendarSelect from "./CourseCalendarSelect";
import SubjectSelectInput from "./new-classroom/SubjectSelectInput";

const SetupField = styled.div``;
const SetupFieldInput = styled.div`
  max-width: 35rem;
`;
const SetupButton = styled.div``;
const SetupText = styled.div`
  font-size: 1.3rem;
  color: #777;
`;

const ClassroomSetupPrompt = (props: {
  data: Classroom;
  allowedSubjects?: string[];
  onUpdate?: () => void;
}) => {
  const [setupCourse, setSetupCourse] = useState<string>();
  const [setupSubject, setSetupSubject] = useState<string>();
  const [setupGrade, setSetupGrade] = useState<string>();
  const [setupCalendar, setSetupCalendar] = useState<IDSK>();
  const [isSaving, setIsSaving] = useState(false);
  const updateClassroom = useMutationUpdateClassroom();
  const { data: courses } = useQueryGetCoursesBySubject(setupSubject);
  const calendarsQuery = useQueryGetAllCalendarsForCourse(
    courses?.find(({ id }) => id === setupCourse) ?? undefined,
  );

  const mustSelectCalendar =
    calendarsQuery.data && calendarsQuery.data.length > 0;

  const canSetup =
    setupCourse &&
    setupSubject &&
    setupGrade &&
    (!mustSelectCalendar || setupCalendar);

  const handleSetup = async () => {
    if (!props.data?.id) return;
    if (!props.data?.sk) return;
    if (!canSetup) return;

    setIsSaving(true);
    await updateClassroom.mutateAsync({
      id: props.data.id,
      sk: props.data.sk,
      workspaceId: props.data.workspaceId,
      schemaVersion: "2020-01-01",
      calendarId: [setupCalendar?.id, setupCalendar?.sk].join("/"),
      courseId: setupCourse,
      subject: [setupSubject],
      grade: [setupGrade],
    });
    setIsSaving(false);
    props.onUpdate?.();
  };

  return (
    <SetupPrompt>
      <div>
        <svg
          viewBox="0 0 512.00006 512"
          width="36"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="m28.683594 486.425781c-20.789063-20.792969-20.789063-54.5 0-75.292969l123.558594-123.558593 75.292968 75.292969-123.558594 123.558593c-20.789062 20.792969-54.5 20.792969-75.292968 0zm0 0"
            fill="#ff6977"
          />
          <path
            d="m366.804688 198.332031-151.902344 151.898438-50.019532-50.019531 151.898438-151.902344zm0 0"
            fill="#eff3f3"
          />
          <path
            d="m236.480469 83.804688-21.609375-21.609376 25.777344-25.78125c35.199218-35.199218 92.269531-35.199218 127.46875 0l80.300781 80.300782-7.898438 14.480468 16.027344 16.03125 17.699219-4.679687 23.175781 23.171875c6.101563 6.105469 6.101563 16 0 22.105469l-48.324219 48.324219c-6.105468 6.101562-16 6.101562-22.105468 0l-23.171876-23.175782 4.679688-17.699218-16.03125-16.027344-25.667969 19.085937-50.023437-50.023437c11.632812-11.632813 11.632812-30.492188 0-42.125l-22.378906-22.378906c-15.992188-15.992188-41.925782-15.992188-57.917969 0zm0 0"
            fill="#8690a6"
          />
          <path
            d="m486.386719 486.386719c20.792969-20.792969 20.792969-54.5 0-75.292969l-131.761719-131.761719-75.292969 75.292969 131.761719 131.761719c20.792969 20.792969 54.5 20.792969 75.292969 0zm0 0"
            fill="#ffcb7c"
          />
          <path
            d="m243.632812 296.207031 52.574219-52.574219c6.273438-6.273437 16.445313-6.273437 22.71875 0l35.699219 35.699219-75.292969 75.292969-35.699219-35.699219c-6.273437-6.273437-6.273437-16.445312 0-22.71875zm0 0"
            fill="#fff"
          />
          <path
            d="m288.742188 251.09375-37.644532 37.648438-186.347656-186.347657 37.644531-37.648437zm0 0"
            fill="#eff3f3"
          />
          <path
            d="m10 36.667969 26.667969-26.667969 48.75 26.789062 16.972656 27.957032-37.644531 37.644531-27.957032-16.972656zm0 0"
            fill="#8690a6"
          />
          <path d="m361.703125 272.269531c0-.003906-.003906-.007812-.007813-.007812l-27.339843-27.339844 39.003906-39.003906 18.148437-13.496094 5.855469 5.855469-3.210937 12.136718c-.910156 3.445313.082031 7.109376 2.597656 9.628907l23.175781 23.171875c4.839844 4.84375 11.277344 7.507812 18.121094 7.507812 6.847656 0 13.28125-2.664062 18.125-7.507812l48.320313-48.320313c4.84375-4.84375 7.507812-11.277343 7.507812-18.125 0-6.847656-2.664062-13.28125-7.507812-18.121093l-23.171876-23.175782c-2.519531-2.519531-6.1875-3.507812-9.628906-2.597656l-12.136718 3.210938-6.671876-6.671876 4.3125-7.914062c2.125-3.894531 1.429688-8.722656-1.707031-11.859375l-80.300781-80.300781c-39.042969-39.039063-102.570312-39.039063-141.609375 0l-25.777344 25.78125c-3.90625 3.90625-3.90625 10.238281 0 14.140625l21.605469 21.609375c1.878906 1.875 4.421875 2.929687 7.074219 2.929687 2.648437 0 5.191406-1.054687 7.070312-2.929687 5.84375-5.84375 13.621094-9.066406 21.890625-9.066406 8.265625 0 16.039063 3.222656 21.886719 9.066406l22.378906 22.378906c7.714844 7.714844 7.714844 20.269531 0 27.980469l-58.339843 58.34375-141.054688-141.054688-16.347656-26.917969c-.910156-1.5-2.195313-2.730468-3.730469-3.574218l-48.75-26.789063c-3.902344-2.144531-8.746094-1.457031-11.890625 1.691407l-26.664062 26.667968c-3.144532 3.144532-3.835938 7.988282-1.691407 11.886719l26.789063 48.75c.84375 1.535156 2.074218 2.820313 3.574218 3.730469l26.925782 16.351562 141.050781 141.050782-34.699219 34.699218-5.566406-5.5625c-3.90625-3.90625-10.234375-3.90625-14.140625 0l-123.558594 123.558594c-24.65625 24.65625-24.65625 64.777344 0 89.433594 12.328125 12.328125 28.523438 18.492187 44.71875 18.492187 16.195313 0 32.390625-6.164062 44.71875-18.492187l123.558594-123.558594c3.90625-3.902344 3.90625-10.234375 0-14.140625l-5.5625-5.566406 15.875-15.875 70.8125 70.8125c3.90625 3.902343 10.238281 3.902343 14.144531 0 3.902344-3.90625 3.902344-10.238281 0-14.140625l-36.402344-36.40625 61.152344-61.148438 124.691406 124.691406c8.167969 8.167969 12.664063 19.027344 12.664063 30.574219 0 11.550781-4.496094 22.410157-12.664063 30.578125-16.859374 16.855469-44.292968 16.855469-61.152343 0l-24.652344-24.652344c-3.90625-3.90625-10.234375-3.90625-14.140625 0-3.90625 3.902344-3.90625 10.234376 0 14.140626l24.652344 24.652343c12.328125 12.328125 28.523437 18.492188 44.71875 18.492188 16.195312 0 32.386718-6.164063 44.714844-18.492188 11.941406-11.941406 18.519531-27.824219 18.519531-44.714843 0-16.890626-6.578125-32.773438-18.523438-44.71875zm-111 39.585938c-2.367187-2.363281-2.367187-6.214844 0-8.578125l7.460937-7.464844 37.648438-37.644531 7.464844-7.464844c1.144531-1.144531 2.667968-1.777344 4.289062-1.777344 1.617188 0 3.140625.632813 4.285156 1.777344l28.628907 28.628906-61.152344 61.152344zm-171.816406-209.464844 23.503906-23.503906 172.210937 172.210937-23.507812 23.503906zm222.585937-25.65625c-17.449218-17.445313-44.503906-19.574219-64.296875-6.375l-8.164062-8.164063 18.710937-18.707031c31.242188-31.246093 82.078125-31.246093 113.324219 0l75.011719 75.007813-4.316406 7.910156c-2.125 3.898438-1.429688 8.722656 1.707031 11.859375l16.03125 16.03125c2.519531 2.519531 6.183593 3.507813 9.625 2.597656l12.140625-3.210937 19.105468 19.109375c1.0625 1.0625 1.648438 2.476562 1.648438 3.980469 0 1.5-.585938 2.914062-1.648438 3.976562l-48.324218 48.324219c-1.0625 1.066406-2.476563 1.652343-3.976563 1.652343-1.503906 0-2.917969-.585937-3.980469-1.652343l-19.109374-19.105469 3.210937-12.136719c.910156-3.445312-.082031-7.109375-2.597656-9.628906l-16.027344-16.027344c-3.511719-3.511719-9.058594-3.917969-13.039063-.957031l-18.738281 13.933594-37.746093-37.742188c8.960937-15.246094 6.902343-35.226562-6.171876-48.296875zm15.308594 85.714844 17.9375 17.941406 17.941406 17.941406-33.375 33.375c-3.589844-1.8125-7.582031-2.777343-11.71875-2.777343-6.964844 0-13.507812 2.710937-18.433594 7.632812l-.390624.390625-23.234376-23.230469zm-294.402344-124.019531 16.050782-16.050782 39.828124 21.886719 11.511719 18.960937-26.546875 26.542969-18.957031-11.511719zm74.527344 440.925781c-16.855469 16.859375-44.292969 16.859375-61.152344 0-16.855468-16.859375-16.855468-44.292969 0-61.152344l116.488282-116.484375 5.554687 5.554688c.003906 0 .007813.003906.007813.007812l50.023437 50.023438 5.566406 5.5625zm82.113281-179.144531 34.699219-34.699219 23.234375 23.230469-.394531.394531c-8.140625 8.140625-9.753906 20.375-4.847656 30.144531l-16.8125 16.808594zm0 0" />
          <path d="m354.625 419.914062c-2.640625 0-5.207031 1.070313-7.070312 2.929688-1.867188 1.859375-2.929688 4.4375-2.929688 7.070312 0 2.640626 1.0625 5.207032 2.929688 7.066407 1.863281 1.871093 4.441406 2.933593 7.070312 2.933593 2.632812 0 5.199219-1.0625 7.070312-2.933593 1.859376-1.859375 2.929688-4.4375 2.929688-7.066407 0-2.632812-1.070312-5.199218-2.929688-7.070312-1.859374-1.859375-4.4375-2.929688-7.070312-2.929688zm0 0" />
        </svg>
      </div>
      <VSpace style={{ gap: "0.5rem" }}>
        <SetupPrompt.Title>Let's get your classroom setup!</SetupPrompt.Title>
      </VSpace>
      <VSpace>
        <SetupPrompt.Text>
          Which subject are you teaching in this classroom?
        </SetupPrompt.Text>
        <SetupField>
          <SetupFieldInput>
            <SubjectSelectInput
              value={setupSubject}
              onChange={(value: any) => setSetupSubject(value.value)}
              allowedSubjects={props.allowedSubjects}
            />
          </SetupFieldInput>
        </SetupField>
      </VSpace>
      <VSpace>
        <SetupText>Which course are you teaching in this classroom?</SetupText>
        <SetupField>
          <SetupFieldInput>
            <SelectInput
              disabled={!setupSubject}
              value={setupCourse}
              onChange={(value) => setSetupCourse(value?.value)}
              options={(courses ?? [])
                .filter((course: Course) => {
                  // console.log("filtering", course);
                  if (setupSubject && course.subject?.includes(setupSubject)) {
                    return true;
                  }
                  return false;
                })
                .sort((a: Course, b: Course) => {
                  const aSeq = a.sequence ?? "";
                  const bSeq = b.sequence ?? "";

                  return aSeq?.localeCompare(bSeq);
                })
                .map((course: Course) => ({
                  label: course.name || "",
                  value: course.id,
                }))}
            />
          </SetupFieldInput>
        </SetupField>
      </VSpace>

      {mustSelectCalendar && (
        <VSpace>
          <SetupText>
            Which calendar would you like to use for this classroom?
          </SetupText>
          <SetupField>
            <SetupFieldInput>
              <CourseCalendarSelect
                value={setupCalendar}
                onChange={(value) => {
                  setSetupCalendar(value);
                }}
                course={courses?.find(({ id }) => id === setupCourse)}
              />
            </SetupFieldInput>
          </SetupField>
        </VSpace>
      )}

      <VSpace>
        <SetupText>
          What is the grade level of{" "}
          <span style={{ textDecoration: "underline" }}>this classroom</span>?
        </SetupText>
        <SetupField>
          <SetupFieldInput>
            <SelectInput
              disabled={!setupCourse}
              value={setupGrade}
              onChange={(value) => setSetupGrade(value?.value)}
              options={(gradeLevelList ?? []).map((grade) => ({
                label: gradeLevelMeta[grade].label,
                value: grade,
              }))}
            />
          </SetupFieldInput>
        </SetupField>
      </VSpace>
      <SetupButton>
        <Button
          disabled={!canSetup}
          isLoading={isSaving}
          color="primary"
          title="Submit"
          onClick={handleSetup}
        >
          Setup Classroom
        </Button>
      </SetupButton>
    </SetupPrompt>
  );
};

export default ClassroomSetupPrompt;
